import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const [subscriptionCache, setSubscriptionCache] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingStates, setLoadingStates] = useState({});

  // Helper to get current user ID from localStorage/session
  const getCurrentUserId = useCallback(() => {
    try {
      // Check localStorage first (common auth pattern)
      const authData = localStorage.getItem('auth_data') || 
                       localStorage.getItem('user') || 
                       sessionStorage.getItem('auth_data');
      
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.user?.id || parsed.id || parsed.userId;
      }
      
      // Check for token that might contain user ID
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        // If using JWT, decode to get user ID (basic implementation)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload.userId || payload.sub || payload.id;
        } catch {
          return null;
        }
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to get user ID:', error);
      return null;
    }
  }, []);

  const getSubscription = useCallback(async (userId = null) => {
    const targetUserId = userId || getCurrentUserId();
    
    if (!targetUserId) {
      console.warn('Subscription check: No user ID available');
      return { status: 'no_user', active: false, tier: 'free' };
    }

    // Check cache first
    if (subscriptionCache[targetUserId] && 
        subscriptionCache[targetUserId].timestamp > Date.now() - 60000) { // 1 min cache
      return subscriptionCache[targetUserId].data;
    }

    // Prevent duplicate requests
    if (loadingStates[targetUserId]) {
      return loadingStates[targetUserId];
    }

    setLoadingStates(prev => ({ ...prev, [targetUserId]: true }));

    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions/status`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': targetUserId
        },
        cache: 'no-cache'
      });
      
      let data;
      if (response.ok) {
        data = await response.json();
        const cacheEntry = {
          data: {
            ...data,
            userId: targetUserId,
            lastChecked: new Date().toISOString()
          },
          timestamp: Date.now()
        };
        
        setSubscriptionCache(prev => ({
          ...prev,
          [targetUserId]: cacheEntry
        }));
        
        return cacheEntry.data;
      } else {
        // Return default subscription state on error
        data = {
          status: 'inactive',
          active: false,
          tier: 'free',
          userId: targetUserId,
          lastChecked: new Date().toISOString(),
          error: response.status
        };
        
        const cacheEntry = { data, timestamp: Date.now() };
        setSubscriptionCache(prev => ({
          ...prev,
          [targetUserId]: cacheEntry
        }));
        
        return data;
      }
    } catch (error) {
      console.error('Subscription fetch error:', error);
      
      // Fallback data
      const fallbackData = {
        status: 'error',
        active: false,
        tier: 'free',
        userId: targetUserId,
        lastChecked: new Date().toISOString(),
        error: 'network_error'
      };
      
      const cacheEntry = { data: fallbackData, timestamp: Date.now() };
      setSubscriptionCache(prev => ({
        ...prev,
        [targetUserId]: cacheEntry
      }));
      
      return fallbackData;
    } finally {
      setLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[targetUserId];
        return newState;
      });
    }
  }, [subscriptionCache, loadingStates, getCurrentUserId]);

  const refreshSubscription = useCallback(async () => {
    const userId = getCurrentUserId();
    if (userId) {
      // Invalidate cache
      setSubscriptionCache(prev => {
        const newCache = { ...prev };
        delete newCache[userId];
        return newCache;
      });
      
      return await getSubscription(userId);
    }
    return null;
  }, [getCurrentUserId, getSubscription]);

  const clearSubscriptionCache = useCallback(() => {
    setSubscriptionCache({});
    setLoadingStates({});
  }, []);

  // Auto-initialize if user is detected
  useEffect(() => {
    if (!isInitialized) {
      const userId = getCurrentUserId();
      if (userId) {
        setIsInitialized(true);
        // Silent initialization - don't await, let it run in background
        getSubscription(userId).catch(() => {});
      }
    }
  }, [isInitialized, getCurrentUserId, getSubscription]);

  // Listen for auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      const userId = getCurrentUserId();
      if (!userId) {
        // User logged out - clear cache
        clearSubscriptionCache();
      } else if (!subscriptionCache[userId]) {
        // User logged in - fetch subscription
        getSubscription(userId).catch(() => {});
      }
    };

    // Listen for storage events (cross-tab auth changes)
    window.addEventListener('storage', handleAuthChange);
    
    // Poll for auth changes (every 30 seconds)
    const pollInterval = setInterval(handleAuthChange, 30000);

    return () => {
      window.removeEventListener('storage', handleAuthChange);
      clearInterval(pollInterval);
    };
  }, [getCurrentUserId, subscriptionCache, getSubscription, clearSubscriptionCache]);

  const value = {
    getSubscription,
    refreshSubscription,
    clearSubscriptionCache,
    subscriptionCache: Object.keys(subscriptionCache).reduce((acc, key) => {
      acc[key] = subscriptionCache[key].data;
      return acc;
    }, {}),
    isLoading: (userId = null) => {
      const targetUserId = userId || getCurrentUserId();
      return !!loadingStates[targetUserId];
    }
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
}